import type { RelationshipStatus } from './birthForm'
import type { GoodDayPurpose } from './guide'
import type { CoinProduct } from '../config/coins'

/**
 * **지금 보는 사주의 사람 — 한 곳에서**.
 *
 * 예전에는 "몇 살인가·결혼했나" 를 화면마다 따로 물었다 (20살 아래 판단 36개 파일, 기혼 판단 12개 파일).
 * 그래서 리포트를 기혼에 맞춰도 궁합·타로·좋은 날짜는 연애 그대로 남아 한 사람 화면 안에서 서로 어긋났다.
 * 이제 나이 갈래·연애 상태 묶음과 **무엇을 보여 줄지**를 이 파일이 정하고, 화면·서버·PDF 는 이 파일만 묻는다.
 * 이 파일 밖에서 `ageBand < 20`·`=== 'married'` 같은 판단을 새로 쓰면 `tests/viewer15.test.ts` 가 막는다.
 *
 * 나이는 만 나이든 5년 구간(`ageBand`, 25 = 25~29살)이든 받는다 — 갈래 경계(10·20·50·65)가 모두 5의 배수라 같은 갈래가 나온다.
 */

/** 나이 갈래 — 10살 아래 / 10~19 / 20~49 / 50~64 / 65 이상 / 모름 */
export type AgeGroup = 'child' | 'teen' | 'adult' | 'senior' | 'elder' | 'unknown'
/** 연애 상태 묶음 — 복잡해요·말하고 싶지 않아요·고르지 않음은 other */
export type StatusGroup = 'single' | 'dating' | 'married' | 'other'

type Age = number | null | undefined

export function ageGroupOf(age: Age): AgeGroup {
  if (age === null || age === undefined || !Number.isFinite(age)) return 'unknown'
  if (age < 10) return 'child'
  if (age < 20) return 'teen'
  if (age < 50) return 'adult'
  if (age < 65) return 'senior'
  return 'elder'
}

/** 20살 아래 (10살 아래 포함) */
export function isYouthAge(age: Age): age is number {
  return age !== null && age !== undefined && age < 20
}
/** 10살 아래 */
export function isChildAge(age: Age): age is number {
  return age !== null && age !== undefined && age < 10
}
/** 50살 이상 (65살 이상 포함) */
export function isSeniorAge(age: Age): age is number {
  return age !== null && age !== undefined && age >= 50
}
/** 65살 이상 */
export function isElderAge(age: Age): age is number {
  return age !== null && age !== undefined && age >= 65
}
/** 20~64살이 아니다 (어린이·청소년 또는 65살 이상) — 기본 문장을 나이에 맞게 바꾸는 자리 */
export function isAgeSpecific(age: Age): age is number {
  return isYouthAge(age) || isElderAge(age)
}

export function statusGroupOf(status: RelationshipStatus | StatusGroup | string | null | undefined): StatusGroup {
  return status === 'single' || status === 'dating' || status === 'married' ? status : 'other'
}
export function isMarried(status: string | null | undefined): boolean {
  return status === 'married'
}

export interface Viewer {
  age: AgeGroup
  /** 5년 구간 (모르면 null) */
  ageBand: number | null
  status: StatusGroup
  sex: 'female' | 'male' | null
  hourKnown: boolean
}

export function viewerOf(input: { age?: Age; ageBand?: Age; status?: string | null; sex?: 'female' | 'male' | null; hourUnknown?: boolean }): Viewer {
  const raw = input.ageBand ?? input.age ?? null
  const ageBand = raw === null || raw === undefined ? null : Math.floor(raw / 5) * 5
  return { age: ageGroupOf(raw), ageBand, status: statusGroupOf(input.status), sex: input.sex ?? null, hourKnown: !input.hourUnknown }
}

const youth = (v: Viewer) => v.age === 'child' || v.age === 'teen'
/** 기혼으로 보여 줄 사람 — 20살 아래는 나이 갈래가 먼저다 */
export function marriedView(v: Viewer): boolean {
  return !youth(v) && v.status === 'married'
}

/*
 * ───────── 무엇을 보여 줄지 (표 하나) ─────────
 */

/**
 * "연애" 라는 이름을 쓰는 자리(오늘의 운세 칸·배경화면 종류·10장 타로 주제·12개월 표·"언제 잘 풀리고")의 이름.
 * 기혼이면 "부부" (`coupleLabel`), 20살 아래는 그 화면의 어린이 이름(`youth`), 그 밖은 원래 이름.
 */
export type LoveName = 'love' | 'couple' | 'youth'
export function loveNameOf(v: Viewer): LoveName {
  if (youth(v)) return 'youth'
  return v.status === 'married' ? 'couple' : 'love'
}

/** 리포트 장 이름 갈래 — `sectionTitle.ts` 가 이 값으로 제목·소제목·묶음 이름을 고른다 */
export type ReportFrame = 'child' | 'teen' | 'married' | 'default'
export function reportFrameOf(v: Viewer): ReportFrame {
  if (v.age === 'child') return 'child'
  if (v.age === 'teen') return 'teen'
  return v.status === 'married' ? 'married' : 'default'
}

/** 질문 타로의 나이 묶음 (질문 목록·요금 안내) */
export type QuestionAudience = 'youth' | 'adult' | 'senior'
export function questionAudienceOf(v: Viewer): QuestionAudience {
  if (youth(v)) return 'youth'
  return v.age === 'senior' || v.age === 'elder' ? 'senior' : 'adult'
}

/*
 * (14:27): **결혼·나이 갈래는 리포트 안에서만.** "기본 리포트만 기혼은 연애 관련 빼고 … 나머지 궁합·타로·기타
 * 등등은 그대로 둘 것. 20살 아래도 그냥 둬." — 15·16 에서 리포트 밖(질문 타로 묶음·궁합 관계·10장 타로 주제 이름·오늘의 운세·
 * 배경화면·홈 카드 설명)에 넣었던 갈래를 걷었다. 그 화면들은 누구에게나 15 전과 같다.
 */

/** 좋은 날짜 다섯 탭 — 기혼은 데이트 자리에 "가족과 함께" */
export function goodDayPurposesOf(v: Viewer): readonly GoodDayPurpose[] {
  return ['move', 'contract', 'interview', 'travel', marriedView(v) ? 'family' : 'date']
}

/**
 * 공부·취업 화면에서 파는 상품 (탭 구분 없이). 50살 이상은 공부 화면이 "아이의 공부" 고르기라 자기 상품이 없다.
 * 20살 아래는 "진로" 탭에서 발표·면접 날(interviewDay)·새 학기 첫날(startDay)을 판다 — 요금 안내도 같은 기준.
 */
const STUDY_ALL: readonly CoinProduct[] = ['examDay', 'interviewDay', 'startDay', 'flow12', 'fitStyle', 'aptitude', 'envFit', 'semesterFriends', 'dayCard', 'tiredCard']
export function studyProductsOf(v: Viewer): readonly CoinProduct[] {
  return v.age === 'senior' || v.age === 'elder' ? [] : STUDY_ALL
}

/** 요금 안내에 보이는 상품 — 화면에서 실제로 살 수 있는 것과 같은 기준 */
export function pricingProductVisible(v: Viewer, product: CoinProduct): boolean {
  if ((STUDY_ALL as readonly string[]).includes(product)) return studyProductsOf(v).includes(product)
  // 궁합 관계는 누구나 전체 목록이라 연애 궁합도 누구에게나 보인다
  return true
}

/** 리포트 입력(서버가 채운 5년 구간·상태 묶음·성별)에서 보는 사람 */
export function viewerOfReport(input: { ageBand?: number | null; status?: string | null; sex?: 'female' | 'male' | null; pillars?: { hour: string | null } }): Viewer {
  return viewerOf({ ageBand: input.ageBand ?? null, status: input.status ?? null, sex: input.sex ?? null, hourUnknown: input.pillars ? input.pillars.hour === null : false })
}

/** 10장 타로 주제 — 20살 아래는 고민·사람·그 밖, 50살 이상은 일 없이 (주제 이름의 "연애" 는 `loveNameOf`) */
export function spreadTopicVisible(v: Viewer, topic: 'love' | 'work' | 'money' | 'choice' | 'people' | 'other'): boolean {
  const audience = questionAudienceOf(v)
  if (audience === 'youth') return topic === 'choice' || topic === 'people' || topic === 'other'
  if (audience === 'senior') return topic !== 'work'
  return true
}
